import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  TextInput,
  Textarea,
  Group,
  Stack,
  Title,
  Text,
  Card,
} from "@mantine/core";
import { Dropzone, IMAGE_MIME_TYPE, FileWithPath } from "@mantine/dropzone";
import { useForm } from "@mantine/form";
import {
  PerformRequest,
  usePerformRequest,
} from "../../../lib/usePerformRequest";
import { Endpoints } from "../../../lib/endpoints";
import { useToastNotification } from "../../../hooks/useToast";
import { IOrganizationProps } from "../../../types/Organization";
import { ExternalLink, Calendar, Globe, Edit2 } from "lucide-react";
import { Loader } from "../../../components/ui/loader";
import { useNavigate } from "react-router-dom";
import "./styles.scss";

const OrganizationsPage: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingOrgId, setEditingOrgId] = useState<number | null>(null);
  const [loadingOrgDetails, setLoadingOrgDetails] = useState(false);
  const toast = useToastNotification();
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [existingLogoUrl, setExistingLogoUrl] = useState<string | null>(null);
  const navigate = useNavigate();

  const endpoint = Endpoints.GetAllOrganizations();
  const {
    data: orgsResponse,
    isLoading: isLoadingOrgs,
    reloadData,
  } = usePerformRequest<any>({
    method: endpoint.method as any,
    route: endpoint.route,
  });

  // Handle different response structures
  const organizations: IOrganizationProps[] = React.useMemo(() => {
    if (!orgsResponse) return [];
    if (Array.isArray(orgsResponse)) return orgsResponse;
    if (orgsResponse.data && Array.isArray(orgsResponse.data))
      return orgsResponse.data;
    if (orgsResponse.results && Array.isArray(orgsResponse.results))
      return orgsResponse.results;
    return [];
  }, [orgsResponse]);

  const [slug, setSlug] = useState("");
  const [originalSlug, setOriginalSlug] = useState("");
  const [isUpdatingSlug, setIsUpdatingSlug] = useState(false);

  const form = useForm({
    initialValues: {
      name: "",
      description: "",
      website: "",
      email: "",
      logo: null as FileWithPath | null,
    },
    validate: {
      name: (value: string) =>
        value.trim().length < 2 ? "Name must be at least 2 characters" : null,
      description: (value: string) =>
        value.trim().length < 10
          ? "Description must be at least 10 characters"
          : null,
      website: (value: string) => {
        if (!value.trim()) return null;
        try {
          new URL(value);
          return null;
        } catch {
          return "Please enter a valid URL";
        }
      },
      email: (value: string) => {
        if (!value.trim()) return "Email is required";
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(value) ? null : "Enter a valid email address";
      },
    },
  });

  useEffect(() => {
    if (form.values.logo) {
      const url = URL.createObjectURL(form.values.logo);
      setLogoPreview(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    } else {
      setLogoPreview(null);
    }
  }, [form.values.logo]);

  const fetchOrganizationDetails = async (orgId: number) => {
    setLoadingOrgDetails(true);
    try {
      const endpoint = Endpoints.GetSingleOrganization(orgId);
      const response = await PerformRequest<IOrganizationProps>({
        method: endpoint.method as any,
        route: endpoint.route,
      });

      if (response.status === 200 && response.data) {
        const org = response.data as any;
        const orgData = org.data || org;

        form.setValues({
          name: orgData.name || "",
          description: orgData.description || "",
          website: orgData.website || "",
          email: orgData.email || "",
          logo: null,
        });

        if (orgData.logo) {
          setExistingLogoUrl(orgData.logo);
        }

        // Set slug for editing
        const currentSlug = orgData.slug || "";
        setSlug(currentSlug);
        setOriginalSlug(currentSlug);

        setEditingOrgId(orgId);
        setIsModalOpen(true);
      } else {
        toast.error("Failed to load organization details.");
      }
    } catch (error) {
      toast.error("An error occurred while loading organization details.");
    } finally {
      setLoadingOrgDetails(false);
    }
  };

  const handleEdit = (orgId: number) => {
    fetchOrganizationDetails(orgId);
  };

  const handleUpdateSlug = async () => {
    if (!editingOrgId) {
      toast.error("No organization selected for editing.");
      return;
    }

    if (!slug.trim()) {
      toast.error("Please enter a slug.");
      return;
    }

    // Validate slug format (lowercase, alphanumeric, hyphens only)
    const slugRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
    if (!slugRegex.test(slug.trim())) {
      toast.error(
        "Slug must contain only lowercase letters, numbers, and hyphens. It cannot start or end with a hyphen."
      );
      return;
    }

    setIsUpdatingSlug(true);
    try {
      const formData = new FormData();
      formData.append("slug", slug.trim());

      const endpoint = Endpoints.GetUpdateOrganizationSlug(editingOrgId);
      const response = await PerformRequest<any>({
        method: endpoint.method as any,
        route: endpoint.route,
        data: formData,
      });

      if (response.status === 200 || response.status === 201) {
        toast.success("Organization slug updated successfully!");
        setOriginalSlug(slug.trim());
        reloadData();
      } else {
        toast.error(
          response.message || "Failed to update slug. Please try again."
        );
      }
    } catch (error) {
      toast.error("An error occurred while updating the slug.");
    } finally {
      setIsUpdatingSlug(false);
    }
  };

  const handleSubmit = async (values: typeof form.values) => {
    if (!editingOrgId) {
      toast.error("No organization selected for editing.");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("name", values.name);
      formData.append("description", values.description);
      formData.append("email", values.email);
      if (values.website) {
        formData.append("website", values.website);
      }
      if (values.logo) {
        formData.append("logo", values.logo);
      }

      const endpoint = Endpoints.GetUpdateOrganization(editingOrgId);
      const response = await PerformRequest<IOrganizationProps>({
        method: endpoint.method as any,
        route: endpoint.route,
        data: formData,
      });

      if (response.status === 200) {
        toast.success("Organization updated successfully!");
        form.reset();
        setLogoPreview(null);
        setExistingLogoUrl(null);
        setEditingOrgId(null);
        setIsModalOpen(false);
        reloadData();
      } else {
        toast.error("Failed to update organization. Please try again.");
      }
    } catch (error) {
      toast.error("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="organization-page">
      <div className="organization-page__header">
        <div>
          <Title order={1}>Organizations</Title>
          <Text c="dimmed" mt="xs">
            Manage your organizations and their settings
          </Text>
        </div>
        <Button
          onClick={() => navigate("/d/organization/new")}
          size="lg"
          style={{ backgroundColor: "#17265a" }}
        >
          Create Organization
        </Button>
      </div>

      {isLoadingOrgs ? (
        <div className="organizations-loading">
          <Loader size="lg" />
          <Text c="dimmed" mt="md">
            Loading organizations...
          </Text>
        </div>
      ) : organizations && organizations.length > 0 ? (
        <div className="organizations-grid">
          {organizations.map((org) => {
            const organizationSubdomainUrl = org.slug
              ? `https://${org.slug}.aiserwave.com`
              : null;
            return (
              <Card
                key={org.id}
                className="organization-card"
                shadow="md"
                padding="lg"
                radius="md"
                withBorder
                onClick={() => navigate(`/d/organizations/${org.id}`)}
                style={{ cursor: "pointer" }}
              >
                <div className="organization-card__content">
                  <div className="organization-card__header">
                    <div className="organization-card__logo">
                      {org.logo ? (
                        <img src={org.logo} alt={org.name} />
                      ) : (
                        <div className="organization-card__logo-placeholder">
                          {org.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className="organization-card__meta">
                      <Title order={3} className="organization-card__name">
                        {org.name}
                      </Title>
                      {org.website && (
                        <a
                          href={org.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="organization-card__website"
                        >
                          <Globe size={14} />
                          <span>Visit website</span>
                        </a>
                      )}
                    </div>
                  </div>

                  {org.description && (
                    <Text
                      c="dimmed"
                      size="sm"
                      className="organization-card__description"
                      lineClamp={3}
                    >
                      {org.description}
                    </Text>
                  )}

                  <div className="organization-card__footer">
                    <div className="organization-card__dates">
                      <div className="organization-card__date">
                        <Calendar size={14} />
                        <Text size="xs" c="dimmed">
                          Created{" "}
                          {new Date(org.created_at).toLocaleDateString()}
                        </Text>
                      </div>
                    </div>
                    <div className="organization-card__actions">
                      <Button
                        variant="subtle"
                        size="xs"
                        leftSection={<Edit2 size={14} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(org.id);
                        }}
                        className="organization-card__edit-btn"
                      >
                        Edit
                      </Button>
                      {organizationSubdomainUrl && (
                        <a
                          href={organizationSubdomainUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="organization-card__link"
                          onClick={(e) => e.stopPropagation()}
                          title={`Visit ${org.name} on Aiserwave`}
                        >
                          <ExternalLink size={16} />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <div className="organizations-empty">
          <Text size="xl" c="dimmed" fw={500}>
            No organizations yet
          </Text>
          <Text c="dimmed" mt="xs">
            Create your first organization to get started
          </Text>
        </div>
      )}

      <Modal
        opened={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          form.reset();
          setLogoPreview(null);
          setExistingLogoUrl(null);
          setEditingOrgId(null);
          setSlug("");
          setOriginalSlug("");
        }}
        title="Edit Organization"
        size="lg"
        centered
      >
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md">
            <TextInput
              label="Organization Name"
              placeholder="Enter organization name"
              required
              {...form.getInputProps("name")}
            />

            <Textarea
              label="Description"
              placeholder="Enter organization description"
              required
              minRows={4}
              {...form.getInputProps("description")}
            />

            <TextInput
              label="Website"
              placeholder="https://example.com"
              {...form.getInputProps("website")}
            />

            <TextInput
              label="Email"
              placeholder="contact@example.com"
              type="email"
              required
              {...form.getInputProps("email")}
            />

            <div>
              <TextInput
                label="Slug"
                placeholder="organization-slug"
                value={slug}
                onChange={(e) => setSlug(e.currentTarget.value)}
                description="Used for your subdomain: https://{slug}.aiserwave.com"
              />
              {slug && (
                <Text size="xs" c="dimmed" mt={4} mb="xs">
                  Preview:{" "}
                  <a
                    href={`https://${slug}.aiserwave.com`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#17265a" }}
                  >
                    https://{slug}.aiserwave.com
                  </a>
                </Text>
              )}
              <Button
                size="sm"
                variant="light"
                onClick={handleUpdateSlug}
                loading={isUpdatingSlug}
                disabled={!slug.trim() || slug.trim() === originalSlug}
                fullWidth
                mt="xs"
                style={{ backgroundColor: "#17265a", color: "white" }}
              >
                {isUpdatingSlug ? "Updating..." : "Update Slug"}
              </Button>
              {slug.trim() === originalSlug && originalSlug && (
                <Text size="xs" c="dimmed" mt={4}>
                  No changes to save
                </Text>
              )}
            </div>

            <div>
              <Text size="sm" fw={500} mb="xs">
                Logo
              </Text>
              {logoPreview ? (
                <div style={{ position: "relative" }}>
                  <img
                    src={logoPreview}
                    alt="Preview"
                    style={{
                      width: "100%",
                      maxHeight: 200,
                      objectFit: "contain",
                      borderRadius: 8,
                      border: "1px solid #e4e7ec",
                    }}
                  />
                  <Button
                    size="xs"
                    variant="subtle"
                    color="red"
                    onClick={() => {
                      form.setFieldValue("logo", null);
                      setLogoPreview(null);
                    }}
                    style={{ position: "absolute", top: 8, right: 8 }}
                  >
                    Remove
                  </Button>
                </div>
              ) : existingLogoUrl ? (
                <div style={{ position: "relative" }}>
                  <img
                    src={existingLogoUrl}
                    alt="Current logo"
                    style={{
                      width: "100%",
                      maxHeight: 200,
                      objectFit: "contain",
                      borderRadius: 8,
                      border: "1px solid #e4e7ec",
                    }}
                  />
                  <Text size="xs" c="dimmed" mt="xs">
                    Current logo. Upload a new image to replace it.
                  </Text>
                  <Dropzone
                    accept={IMAGE_MIME_TYPE}
                    onDrop={(files) => {
                      if (files.length > 0) {
                        form.setFieldValue("logo", files[0]);
                      }
                    }}
                    maxSize={5 * 1024 * 1024}
                    multiple={false}
                    style={{ marginTop: 12 }}
                  >
                    <Group
                      justify="center"
                      gap="xl"
                      style={{ minHeight: 80, pointerEvents: "none" }}
                    >
                      <div>
                        <Text size="sm" inline>
                          Click to upload new logo
                        </Text>
                      </div>
                    </Group>
                  </Dropzone>
                </div>
              ) : (
                <Dropzone
                  accept={IMAGE_MIME_TYPE}
                  onDrop={(files) => {
                    if (files.length > 0) {
                      form.setFieldValue("logo", files[0]);
                    }
                  }}
                  maxSize={5 * 1024 * 1024}
                  multiple={false}
                >
                  <Group
                    justify="center"
                    gap="xl"
                    style={{ minHeight: 120, pointerEvents: "none" }}
                  >
                    <div>
                      <Text size="xl" inline>
                        Drag image here or click to select
                      </Text>
                      <Text size="sm" c="dimmed" inline mt={7}>
                        Upload a logo for your organization (max 5MB)
                      </Text>
                    </div>
                  </Group>
                </Dropzone>
              )}
            </div>

            <Group justify="flex-end" mt="md">
              <Button
                variant="subtle"
                onClick={() => {
                  setIsModalOpen(false);
                  form.reset();
                  setLogoPreview(null);
                  setExistingLogoUrl(null);
                  setEditingOrgId(null);
                  setSlug("");
                }}
                disabled={loading || loadingOrgDetails}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                loading={loading || loadingOrgDetails}
                style={{ backgroundColor: "#17265a" }}
              >
                {editingOrgId ? "Update Organization" : "Create Organization"}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </div>
  );
};

export default OrganizationsPage;
